// Stock Transfers - Step 1: Select Items
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  Info,
  Calendar,
  FileText,
  Save,
  Search,
  Package,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { format } from "date-fns";
import { mockStockItems } from "@/data/mockStockItems";

export interface TransferSetupData {
  reference: string;
  date: string;
  notes: string;
  selectedItems: Array<{
    id: string;
    code: string;
    name: string;
    availableQuantity: number;
  }>;
}

export function StockTransfersStep1Page() {
  const { navigateTo } = useNavigation();
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState<TransferSetupData>({
    reference: `TRF-${format(new Date(), "yyyy-MM-dd")}-${Math.floor(Math.random() * 1000).toString().padStart(3, "0")}`,
    date: format(new Date(), "yyyy-MM-dd"),
    notes: "",
    selectedItems: [],
  });

  // Filter stock items based on search
  const filteredItems = useMemo(() => {
    if (!searchQuery) return mockStockItems;
    
    const query = searchQuery.toLowerCase();
    return mockStockItems.filter(
      (item) =>
        item.code.toLowerCase().includes(query) ||
        item.name.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const handleItemToggle = (item: typeof mockStockItems[0], checked: boolean) => {
    if (checked) {
      setFormData({
        ...formData,
        selectedItems: [
          ...formData.selectedItems,
          {
            id: item.id,
            code: item.code,
            name: item.name,
            availableQuantity: item.quantity || 0,
          },
        ],
      });
    } else {
      setFormData({
        ...formData,
        selectedItems: formData.selectedItems.filter((i) => i.id !== item.id),
      });
    }
  };

  const isItemSelected = (itemId: string) => {
    return formData.selectedItems.some((i) => i.id === itemId);
  };

  const handleNext = () => {
    // Validation
    if (!formData.reference.trim()) {
      toast.error("Please enter a transfer reference");
      return;
    }
    if (!formData.date) {
      toast.error("Please select a transfer date");
      return;
    }
    if (formData.selectedItems.length === 0) {
      toast.error("Please select at least one item to transfer");
      return;
    }

    // Navigate to step 2 with form data
    navigateTo("stock-transfers/create/step2", { setupData: formData });
  };

  const handleSaveAndExit = () => {
    // Validation
    if (!formData.reference.trim()) {
      toast.error("Please enter a transfer reference before saving");
      return;
    }
    if (!formData.date) {
      toast.error("Please select a transfer date before saving");
      return;
    }

    // TODO: Save as draft
    toast.success("Transfer saved as draft! You can resume it later from the Stock Transfers page.");
    navigateTo("stock-transfers");
  };

  return (
    <div className="flex flex-col h-full max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateTo("stock-transfers")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h1 className="text-3xl font-bold tracking-tight">Create Stock Transfer</h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Transfer stock between locations
            </p>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-medium">
              1
            </div>
            <span className="text-sm font-medium">Select Items</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
              2
            </div>
            <span className="text-sm text-muted-foreground">Configure Transfers</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
              3
            </div>
            <span className="text-sm text-muted-foreground">Review & Complete</span>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Select items to transfer
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Choose the stock items you want to transfer. In the next step, you'll configure the source and destination locations for each item.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1">
        {/* Left Column - Transfer Details */}
        <div className="lg:col-span-1">
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Transfer Details</CardTitle>
              <CardDescription>
                Basic information for this transfer
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Reference */}
              <div className="space-y-2">
                <Label htmlFor="reference" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Transfer Reference *
                </Label>
                <Input
                  id="reference"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="TRF-2024-001"
                  required
                />
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label htmlFor="date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Transfer Date *
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes about this transfer..."
                  rows={4}
                />
              </div>

              {/* Selected Items Summary */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Selected Items</span>
                  <Badge variant="secondary">
                    {formData.selectedItems.length}
                  </Badge>
                </div>
                {formData.selectedItems.length > 0 ? (
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {formData.selectedItems.map((item) => (
                      <div
                        key={item.id}
                        className="text-sm p-2 rounded bg-muted/50 flex items-center justify-between"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{item.code}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {item.name}
                          </div>
                        </div>
                        <Badge variant="outline" className="ml-2 flex-shrink-0">
                          {item.availableQuantity}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No items selected yet
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Item Selection */}
        <div className="lg:col-span-2">
          <Card className="h-full flex flex-col">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Select Stock Items</CardTitle>
                  <CardDescription>
                    Choose items to include in this transfer
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by code, name, or category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Items List */}
              <div className="flex-1 border rounded-lg overflow-hidden">
                <div className="max-h-[500px] overflow-y-auto">
                  {filteredItems.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      {searchQuery
                        ? "No items found matching your search"
                        : "No stock items available"}
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                        >
                          <Checkbox
                            id={`item-${item.id}`}
                            checked={isItemSelected(item.id)}
                            onCheckedChange={(checked) =>
                              handleItemToggle(item, checked as boolean)
                            }
                          />
                          <label
                            htmlFor={`item-${item.id}`}
                            className="flex-1 cursor-pointer"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                  <span className="font-medium">{item.code}</span>
                                </div>
                                <div className="text-sm text-muted-foreground mt-1">
                                  {item.name}
                                </div>
                                {item.category && (
                                  <Badge variant="outline" className="mt-2">
                                    {item.category}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="text-sm font-medium">
                                  Available: {item.quantity || 0}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {item.unit || "units"}
                                </div>
                              </div>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t">
        <Button
          variant="outline"
          onClick={() => navigateTo("stock-transfers")}
        >
          Cancel
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAndExit}
          >
            <Save className="h-4 w-4 mr-2" />
            Save as Draft
          </Button>
          <Button onClick={handleNext}>
            Next: Configure Transfers
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
